import type { PalantirLabelColors } from '../../shared/pipes/labels/labels.type';
import type { PalantirIcon } from '../icon/icon.type';
import type { PalantirTableAvatar } from './components/columns/avatar-group/avatar-group.type';
import type { PalantirTableStatus } from './components/columns/status/status.type';

export interface PalantirTableData {
  dataSource: PalantirTableDataSource[];
  displayedColumns: PalantirTableDisplayColumn[];
}

export interface PalantirTableDataSource {
  [key: string]: PalantirTableDataSourceItem;
}

export interface PalantirTableDisplayColumn {
  icon?: PalantirIcon;
  key: string;
  label: string;
  sortable?: boolean;
}

export interface PalantirTableSortEvent {
  active: string;
  direction?: 'asc' | 'desc';
}

export type PalantirTableDataSourceItem =
  | PalantirTableActionItem
  | PalantirTableAvatarGroupItem
  | PalantirTableAvatarItem
  | PalantirTableCustomItem
  | PalantirTableDropdownItem
  | PalantirTableHiddenItem
  | PalantirTableLoadingItem
  | PalantirTableStarsItem
  | PalantirTableStatusItem
  | PalantirTableTagsItem
  | PalantirTableTextItem;

export interface PalantirTableDataSourceItemCommon<
  T extends PalantirTableDataSourceType | PalantirTableDataSourceTypeEnum,
> {
  metadata?: any;
  type: T;
}

export interface PalantirTableActionItem extends PalantirTableDataSourceItemCommon<'ACTION'> {
  action(value: PalantirTableDataSourceItem, event: MouseEvent): Promise<void> | void;
  disabled?: boolean;
  /**
   * @defaultValue 'edit-filled'
   */
  icon?: PalantirIcon;
}

export interface PalantirTableHiddenItem extends PalantirTableDataSourceItemCommon<'HIDDEN'> {
  value?: number | string;
}

export type PalantirTableLoadingItem = PalantirTableDataSourceItemCommon<'LOADING'>;

export interface PalantirTableStarsItem extends PalantirTableDataSourceItemCommon<'STARS'> {
  value?: number;
}

export interface PalantirTableStatusItem
  extends PalantirTableDataSourceItemCommon<'STATUS'>, PalantirTableStatus {}

export interface PalantirTableTextItem extends PalantirTableDataSourceItemCommon<'TEXT'> {
  /**
   * O texto que será exibido quando não houver valor,
   * não há valor significa que o valor é falsy.
   */
  emptyText?: string;
  value?: string;
}

export interface PalantirTableTagsItem extends PalantirTableDataSourceItemCommon<'TAGS'> {
  emptyText?: string;
  tags: {
    color: PalantirLabelColors | string;
    prefix?: PalantirIcon;
    suffix?: PalantirIcon;
    tag: string;
  }[];
}

type PalantirTableAvatarSingle = {
  action?(value: PalantirTableDataSourceItem): Promise<void> | void;
  label: string;
  metadata?: any;
} & ({ icon?: PalantirIcon; value: string } | { icon?: PalantirIcon; value?: string });

export type PalantirTableAvatarItem = PalantirTableAvatarSingle &
  PalantirTableDataSourceItemCommon<'AVATAR'>;

export interface PalantirTableAvatarGroupItem extends PalantirTableDataSourceItemCommon<'AVATAR_GROUP'> {
  action?(value: PalantirTableDataSourceItem): Promise<void> | void;
  extraItemsTooltip?: string;
  maxItems?: number;
  values: PalantirTableAvatar[];
}

export interface PalantirTableDropdownItem extends PalantirTableDataSourceItemCommon<'DROPDOWN'> {
  /**
   * The action to be executed when an option is selected
   *
   * @param value - The value selected in the dropdown
   */
  action(value: number | string | null): Promise<void> | void;
  /**
   * The placeholder of the dropdown
   */
  label: string;
  /**
   * The options of the dropdown
   */
  options: {
    description: string;
    label: number | string;
    value: number | string | null;
  }[];
  /**
   * The value selected in the dropdown
   */
  value: number | string | null;
}

export interface PalantirTableCustomItem {
  metadata?: any;
  type: 'CUSTOM';
}

export enum PalantirTableDataSourceTypeEnum {
  ACTION = 'ACTION',
  AVATAR = 'AVATAR',
  AVATAR_GROUP = 'AVATAR_GROUP',
  CUSTOM = 'CUSTOM',
  DROPDOWN = 'DROPDOWN',
  HIDDEN = 'HIDDEN',
  LOADING = 'LOADING',
  STARS = 'STARS',
  STATUS = 'STATUS',
  TAGS = 'TAGS',
  TEXT = 'TEXT',
}

export type PalantirTableDataSourceType = `${PalantirTableDataSourceTypeEnum}`;
